import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Form, Button, ListGroup,
  Alert, Badge, Modal, Spinner,
} from 'react-bootstrap';
import { IFramework } from '../../interfaces/framework';
import { frameworkService, UpdateFrameworkData } from '../../services/frameworkService';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { notify } from '../../stores/useNotificationStore';

interface IDominio {
  id: number;
  nome: string;
  indice?: string;
}

interface IQuestionario {
  id: number;
  nome: string;
  descricao?: string;
}

const FrameworkRegistration: React.FC = () => {
  const [formData, setFormData] = useState<IFramework>({ nome: '', descricao: '', dominios: [] });
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [companyName, setCompanyName] = useState<string>('');

  // Domínios: somente visualização
  const [dominiosRelacionados, setDominiosRelacionados] = useState<IDominio[]>([]);

  // Questionários
  const [questionariosDisponiveis, setQuestionariosDisponiveis] = useState<IQuestionario[]>([]);
  const [questionariosAssociados, setQuestionariosAssociados] = useState<IQuestionario[]>([]);

  // Modal criação inline de questionário
  const [showModal, setShowModal] = useState(false);
  const [novoQ, setNovoQ] = useState({ nome: '', descricao: '' });
  const [savingQ, setSavingQ] = useState(false);

  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { empresaId, tipoEmpresa } = useInterfaceStore();

  const tipoPessoa: string = (() => {
    try { return JSON.parse(localStorage.getItem('usuarioData') || '{}').tipo_pessoa || ''; }
    catch { return ''; }
  })();
  const canWrite = tipoEmpresa === '4Comply' || ['Administrador', 'DPO'].includes(tipoPessoa);

  // Carrega nome da empresa
  useEffect(() => {
    if (!empresaId) return;
    api.getCompany(empresaId).then(res => {
      if (res.success && res.data) {
        setCompanyName(res.data.nome_fantasia || res.data.razao_social || `Empresa ${empresaId}`);
      }
    });
  }, [empresaId]);

  // Carrega dados do framework em edição
  useEffect(() => {
    if (!id) return;
    setIsEditing(true);
    frameworkService.getById(parseInt(id)).then(fw => {
      if (fw) setFormData(fw);
    });
  }, [id]);

  // Carrega domínios relacionados (somente leitura)
  useEffect(() => {
    if (!id) return;
    api.getFrameworkDominios(parseInt(id)).then(res => {
      if (res.success && Array.isArray(res.data)) setDominiosRelacionados(res.data);
    });
  }, [id]);

  // Carrega questionários
  const fetchQuestionarios = useCallback(async () => {
    if (!id) return;
    const [disponiveis, associados] = await Promise.all([
      frameworkService.getQuestionariosDisponiveis(parseInt(id)),
      frameworkService.getQuestionarios(parseInt(id)),
    ]);
    setQuestionariosDisponiveis(disponiveis);
    setQuestionariosAssociados(associados);
  }, [id]);

  useEffect(() => { fetchQuestionarios(); }, [fetchQuestionarios]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nome.trim()) { notify.warning('Nome é obrigatório.'); return; }
    if (!empresaId) { notify.warning('Selecione uma empresa antes de salvar.'); return; }

    setLoading(true);
    try {
      if (isEditing && formData.id) {
        const updateData: UpdateFrameworkData = {
          id: formData.id,
          nome: formData.nome,
          descricao: formData.descricao,
          dominios: formData.dominios,
        };
        await frameworkService.update(formData.id, updateData);
        notify.success('Framework atualizado com sucesso!');
      } else {
        await frameworkService.create({
          nome: formData.nome,
          descricao: formData.descricao,
          dominios: [],
          empresaId: empresaId,
        });
        notify.success('Framework criado com sucesso!');
      }
      navigate('/app/frameworks/list');
    } catch {
      notify.error('Erro ao salvar framework.');
    } finally {
      setLoading(false);
    }
  };

  const associarQuestionario = async (q: IQuestionario) => {
    try {
      await frameworkService.associarQuestionario(formData.id!, q.id);
      setQuestionariosDisponiveis(prev => prev.filter(x => x.id !== q.id));
      setQuestionariosAssociados(prev => [...prev, q]);
    } catch { notify.error('Erro ao associar questionário.'); }
  };

  const desassociarQuestionario = async (q: IQuestionario) => {
    try {
      await frameworkService.desassociarQuestionario(formData.id!, q.id);
      setQuestionariosAssociados(prev => prev.filter(x => x.id !== q.id));
      setQuestionariosDisponiveis(prev => [...prev, q]);
    } catch { notify.error('Erro ao desassociar questionário.'); }
  };

  const handleCriarQuestionario = async () => {
    if (!novoQ.nome.trim()) { notify.warning('Nome do questionário é obrigatório.'); return; }
    setSavingQ(true);
    try {
      const res = await api.createQuestionnaire({
        nome: novoQ.nome,
        descricao: novoQ.descricao,
        empresa_id: empresaId,
      });
      if (!res.success || !res.data?.id) throw new Error();
      const criado: IQuestionario = { id: res.data.id, nome: novoQ.nome };
      if (formData.id) {
        await frameworkService.associarQuestionario(formData.id, criado.id);
        setQuestionariosAssociados(prev => [...prev, criado]);
      } else {
        setQuestionariosDisponiveis(prev => [...prev, criado]);
      }
      setNovoQ({ nome: '', descricao: '' });
      setShowModal(false);
    } catch {
      notify.error('Erro ao criar questionário.');
    } finally {
      setSavingQ(false);
    }
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex align-items-center gap-3 mb-4">
        <h1 className="mb-0">{isEditing ? 'Editar Framework' : 'Cadastro de Frameworks'}</h1>
        {companyName && <Badge bg="secondary" className="fs-6">{companyName}</Badge>}
      </div>

      {!canWrite && (
        <Alert variant="warning" className="mb-4">
          Você tem acesso somente de <strong>leitura</strong> neste módulo.
        </Alert>
      )}

      {!empresaId && (
        <Alert variant="danger" className="mb-4">
          Nenhuma empresa ativa selecionada. Selecione uma empresa para continuar.
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <fieldset disabled={!canWrite}>

          {/* ── Informações Básicas ─────────────────────────────── */}
          <Card className="mb-4">
            <Card.Header as="h5">Informações Básicas</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3" controlId="nome">
                <Form.Label>Nome <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.nome}
                  onChange={e => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: ISO 27001, LGPD Framework..."
                  required
                />
              </Form.Group>
              <Form.Group controlId="descricao">
                <Form.Label>Descrição</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.descricao}
                  onChange={e => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descreva o objetivo e o escopo do framework"
                />
              </Form.Group>
            </Card.Body>
          </Card>

          {/* ── Domínios Relacionados (somente leitura) ─────────── */}
          <Card className="mb-4">
            <Card.Header as="h5">Domínios Relacionados</Card.Header>
            <Card.Body>
              {!isEditing ? (
                <p className="text-muted mb-0">
                  Salve o framework primeiro; os domínios são vinculados pelo módulo de Domínios e Subdomínios.
                </p>
              ) : dominiosRelacionados.length === 0 ? (
                <p className="text-muted mb-0">
                  Nenhum domínio vinculado. Gerencie os vínculos no módulo de <strong>Domínios e Subdomínios</strong>.
                </p>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {dominiosRelacionados.map(d => (
                    <Badge key={d.id} bg="primary" className="py-2 px-3 fs-6 fw-normal">
                      {d.indice ? `${d.indice} – ` : ''}{d.nome}
                    </Badge>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

        </fieldset>

        {/* ── Questionários Associados (habilitado fora do fieldset) ─ */}
        {isEditing && (
          <Card className="mb-4">
            <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
              Questionários Associados
              {canWrite && (
                <Button variant="outline-primary" size="sm" onClick={() => setShowModal(true)}>
                  + Novo Questionário
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h6 className="text-muted mb-2">Disponíveis</h6>
                  <ListGroup style={{ maxHeight: '240px', overflowY: 'auto' }}>
                    {questionariosDisponiveis.length === 0 && (
                      <ListGroup.Item className="text-muted fst-italic">Nenhum disponível</ListGroup.Item>
                    )}
                    {questionariosDisponiveis.map(q => (
                      <ListGroup.Item key={q.id} className="d-flex justify-content-between align-items-center">
                        <span>{q.nome}</span>
                        {canWrite && (
                          <Button variant="outline-primary" size="sm" onClick={() => associarQuestionario(q)}>+</Button>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Col>
                <Col md={6}>
                  <h6 className="text-muted mb-2">Associados</h6>
                  <ListGroup style={{ maxHeight: '240px', overflowY: 'auto' }}>
                    {questionariosAssociados.length === 0 && (
                      <ListGroup.Item className="text-muted fst-italic">Nenhum associado</ListGroup.Item>
                    )}
                    {questionariosAssociados.map(q => (
                      <ListGroup.Item key={q.id} className="d-flex justify-content-between align-items-center">
                        <span>{q.nome}</span>
                        {canWrite && (
                          <Button variant="outline-danger" size="sm" onClick={() => desassociarQuestionario(q)}>−</Button>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        <div className="text-end">
          <Button variant="secondary" onClick={() => navigate('/app/frameworks/list')} className="me-2">
            Cancelar
          </Button>
          {canWrite && (
            <Button type="submit" disabled={loading || !empresaId}>
              {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar'}
            </Button>
          )}
        </div>
      </Form>

      {/* ── Modal: criação inline de questionário ──────────────── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Novo Questionário</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nome <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={novoQ.nome}
              onChange={e => setNovoQ(prev => ({ ...prev, nome: e.target.value }))}
              placeholder="Nome do questionário"
              autoFocus
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={novoQ.descricao}
              onChange={e => setNovoQ(prev => ({ ...prev, descricao: e.target.value }))}
              placeholder="Descrição opcional"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleCriarQuestionario} disabled={savingQ}>
            {savingQ ? <Spinner as="span" animation="border" size="sm" /> : 'Criar e Associar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FrameworkRegistration;
